<template>
  <v-icon-btn
    v-bind="buttonProps"
    @click="handleClick"
    :style="{ borderRadius: computedBorderRadius, width: computedWidth, backgroundColor: computedBackgroundColor }"
  >
    <v-badge v-if="showCount" :content="itemCount" :value="itemCount > 0" color="var(--kds-success)" overlap>
      <v-icon
        class="custom-icon"
        :size="size"
        :color="computedIconColor"
        :style="{ marginLeft: computedMarginLeft, height: computedIconHeight }"
        aria-hidden="true"
      >
        {{ computedIcon }}
      </v-icon>
    </v-badge>
    <v-icon
      v-else
      class="custom-icon"
      :size="size"
      :color="computedIconColor"
      :style="{ marginLeft: computedMarginLeft, height: computedIconHeight }"
      aria-hidden="true"
    >
      {{ computedIcon }}
    </v-icon>
  </v-icon-btn>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'flat',
    validator: (value) => ['flat', 'outlined', 'text', 'tonal', 'elevated'].includes(value),
  },
  icon: {
    type: String,
    default: 'ri-arrow-right-line',
  },
  size: {
    type: String,
    default: 'default',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: 'primary',
  },
  iconColor: {
    type: String,
    default: '',
  },
  isToggle: {
    type: Boolean,
    default: false,
  },
  initialToggled: {
    type: Boolean,
    default: false,
  },
  density: {
    type: String,
    default: 'default',
    validator: (val) => ['default', 'comfortable', 'compact'].includes(val),
  },
  // Descriptive label announced by screen readers — required for accessibility.
  // Every usage of EgovIconButton should pass a meaningful ariaLabel.
  ariaLabel: {
    type: String,
    default: 'Icon button',
  },
  borderRadius: {
    type: String,
    default: '5%',
  },
  width: {
    type: String,
    default: '',
  },
  iconHeight: {
    type: String,
    default: '',
  },
  marginLeft: {
    type: String,
    default: '0',
  },
  outlinedBackgroundColor: {
    type: String,
    default: 'transparent',
  },
  disabledBackgroundColor: {
    type: String,
    default: '',
  },
  disabledIconColor: {
    type: String,
    default: '',
  },
  showCount: { type: Boolean, default: false },
  itemCount: { type: Number, default: null },
})

const emit = defineEmits(['click', 'toggle'])
const toggled = ref(props.initialToggled)

const handleClick = (event) => {
  emit('click', event)
  if (props.isToggle) {
    toggled.value = !toggled.value
    emit('toggle', toggled.value)
  }
}

const computedIcon = computed(() => {
  if (props.isToggle && toggled.value && props.toggleIcon) {
    return props.toggleIcon
  }
  return props.icon
})

const computedIconColor = computed(() => {
  if (props.disabled) {
    return props.disabledIconColor || (['flat', 'elevated'].includes(props.variant) ? 'white' : props.color)
  }
  return (
    props.iconColor ||
    (['flat', 'elevated'].includes(props.variant) || (props.isToggle && toggled.value) ? 'white' : props.color)
  )
})

const computedBorderRadius = computed(() => props.borderRadius)
const computedWidth = computed(() => props.width)
const computedMarginLeft = computed(() => props.marginLeft)
const computedIconHeight = computed(() => props.iconHeight)

const computedBackgroundColor = computed(() => {
  if (props.disabled) {
    return props.disabledBackgroundColor || (props.variant === 'flat' ? 'grey' : props.outlinedBackgroundColor)
  }
  return props.variant === 'outlined' ? props.outlinedBackgroundColor : props.color
})

const buttonProps = computed(() => {
  return {
    variant: props.variant,
    disabled: props.disabled,
    loading: props.loading,
    color: props.color,
    density: props.density,
    size: props.size,
    // aria-label on the button describes its purpose to screen readers.
    // The inner v-icon uses aria-hidden="true" so only this label is announced.
    'aria-label': props.ariaLabel,
    // Never set aria-hidden on the button itself — a focusable element
    // must not be hidden from assistive technology.
    'aria-hidden': undefined,
    disabledBackgroundColor: props.disabledBackgroundColor,
    ...(props.isToggle && { 'aria-pressed': toggled.value }),
  }
})
</script>

<style lang="scss" scoped>
.v-icon-btn {
  min-width: unset;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: no change;
    border: 1px solid var(--kds-outline-variant, #c5c6d0);
    background: var(--kds-on-surface-variant-12, rgba(0, 98, 158, 0.12));
  }

  &:focus {
    background: var(--kds-on-surface-variant-12, rgba(0, 98, 158, 0.12));
    border: 1px solid var(--kds-focus, #0f62fe);
  }

  &:active {
    border: 1px solid var(--kds-outline, #757680);
    background: var(--kds-on-surface-variant-16, rgba(0, 98, 158, 0.16));
  }

  &.disabled {
    color: rgba(0, 24, 73, 0.38);
  }

  &-danger {
    color: var(--kds-error);
    background-color: transparent;
    border: 1px solid transparent;

    &:hover {
      color: no change;
      border: 1px solid var(--kds-outline-variant, #c5c6d0);
      background: var(--kds-error-12, rgba(0, 98, 158, 0.12));
    }

    &:focus {
      background: var(--kds-error-12, rgba(0, 98, 158, 0.12));
      border: 1px solid var(--kds-focus, #0f62fe);
    }

    &:active {
      border: 1px solid var(--kds-outline, #757680);
      background: var(--kds-error-16, rgba(0, 98, 158, 0.16));
    }

    &.disabled {
      color: rgba(0, 24, 73, 0.38);
    }
  }

  &-primary {
    &:hover {
      color: no change;
      border: 1px solid var(--kds-outline-variant, #c5c6d0);
      background: var(--kds-primary-12, rgba(0, 98, 158, 0.12));
    }

    &:focus {
      background: var(--kds-primary-12, rgba(0, 98, 158, 0.12));
      border: 1px solid var(--kds-focus, #0f62fe);
    }

    &:active {
      border: 1px solid var(--kds-outline, #757680);
      background: var(--kds-primary-16, rgba(0, 98, 158, 0.16));
    }

    &.disabled {
      color: rgba(0, 24, 73, 0.38);
    }
  }

  &.v-btn--variant-flat {
    .custom-icon {
      color: var(--kds-on-primary) !important;
    }
  }

  &.v-btn--variant-elevated {
    .custom-icon {
      color: var(--kds-on-primary) !important;
    }
  }

  &[aria-pressed='true'] {
    .custom-icon {
      color: var(--kds-on-primary) !important;
    }
  }

  &[disabledBackgroundColor] {
    opacity: 100%;
  }

  .custom-icon {
    font-size: 1.25rem;
    line-height: 1;
    transition: all 0.2s ease;

    &--size-x-small {
      font-size: 1rem;
    }

    &--size-small {
      font-size: 60%;
    }

    &--size-large {
      font-size: 1.375rem;
    }

    &--size-x-large {
      font-size: 1.5rem;
    }
  }

  &:focus-visible,
  &:hover,
  &:active {
    outline: 1px solid var(--kds-focus);
    border-radius: 2px;
    outline-offset: 2px;
  }

  @supports not selector(:focus-visible) {
    &:focus {
      outline: 1px solid var(--kds-focus);
      border-radius: 2px;
      outline-offset: 2px;
    }
  }
}
</style>
